/**
 * Custom Element for handling quantity input and price updates based on the quantity and discounts provided by the
 *  Subscription widget.
 *
 *  The responsibility of this element is to:
 *      - Listen for quantity button clicks and
 *          - Update the quantity input value
 *          - Update the displayed prices based on the selected quantity (applying the respective discount), taking into
 *              account the currently selected Subscription Plan with its respective discount
 *      - Listen for Subscription Plan changes and
 *          - Update the displayed prices based on the selected Subscription Plan (applying the respective discount),
 *              taking into account the currently selected quantity with its respective discount
 *
 *  The prices and related data we have to update are:
 *      - in the "Subscribe & Save" section:
 *          - Strike-through "Original" (compare-at) price. It's not a price-per-item, but a total price for the selected quantity
 *          - Actual price (agan, not a price-per-item, but a total price for the selected quantity and the selected Subscription Plan)
 *          - Discount amount (in percent) for the selected Subscription Plan and the selected quantity:
 *              - "Save X%" (on the badge in the top right corner of the Subscription Plan section)
 *              - "Save X% on every order" (the first line in bullet points in the Subscription Plan section)
 *
 *  Data we have from UI:
 *    - Quantity buttons with the following data attributes:
 *      - data-quantity: the quantity value
 *      - data-discount-volume: the discount volume (amount or percentage)
 *      - data-discount-type: the discount type (fixed_amount or percentage)
 *      - data-compare-at-price-per-item: the original (base) price per item
 *      - data-currency-symbol: the currency symbol
 *    - Subscription and One-Time purchase Widget with the following data:
 *      - '.appstle_subscription_amount_wrapper .appstle_subscription_amount .money': the Subscription price (with the Subscription discount)
 *
 *  Data we have to calculate:
 *      - The original (base) total price for the selected quantity (calculated as the original price per item * quantity)
 *      - The One-Time price (calculated as (the original price per item - quantity discount) * quantity)
 *      - The Subscription price (calculated as (the subscription price for the selected plan - quantity discount) * quantity)
 *      - The discount amount in percent (calculated as (1 - (Subscription price / Original total price)) * 100)
 *
 *  The elements we have to update:
 *      - '.appstle_subscription_amount_wrapper .appstle_subscription_amount .money': the Subscription price (with the Subscription discount and the selected quantity discount applied)
 *      - '.appstle_subscription_amount_wrapper .appstle_subscription_compare_amount .money': the Original (base) total price for the selected quantity
 *      - '.appstle_one_time_price_wrapper .appstle_subscription_amount .money': the One-Time price (with selected quantity discount applied)
 *      - '.appstle_one_time_price_wrapper .appstle_subscription_amount_compare_at_price .money': the Original (base) total price for the selected quantity
 *      - '.appstle-save-badge': the badge with the discount amount in percent (the "Save X%" badge)
 *      - '.appstleSelectedSellingPlanOptionDescription -> first Div': the first line in bullet points in the Subscription Plan section (the "Save X% on every order" line)
 */
if (!customElements.get('quantity-input-malaberg')) {
    customElements.define(
        'quantity-input-malaberg',
        class QuantityInputMalaberg extends HTMLElement {
            constructor() {
                super();
                this.debug = false;
                this.input = this.querySelector('input[name="quantity"]');
                this.changeEvent = new Event('change', { bubbles: true });

                this.basePriceStorage = {};
                this.lastSelectedSellingPlan = null;

                this.currencySymbol = this.dataset.currencySymbol || '£';
                this.debug && console.log("Currency Symbol:", this.currencySymbol);

                this.compareAtPricePerItem = parseFloat(this.dataset.compareAtPrice) || 0;
                this.debug && console.log("Compare-At Price Per Item:", this.compareAtPricePerItem);

                // Listen for quantity button clicks
                this.querySelectorAll('button').forEach((button) =>
                    button.addEventListener('click', this.onButtonClick.bind(this))
                );
                this.debug && console.log("Added click event listeners to quantity buttons");

                // Listen for Subscription Plan changes (on the Subscription widget)
                document.addEventListener('AppstleSubscription:SubscriptionWidget:SubscriptionWidgetUpdated', this.onSubscriptionWidgetUpdated.bind(this));
            }

            onSubscriptionWidgetUpdated(event) {
                this.debug && console.log(event);
                this.debug && console.log('Subscription Widget Updated');
                this.updateStoredBasePrices();
                this.recalculateAndUpdatePrices();
            }

            /** Handle quantity button click */
            onButtonClick(event) {
                event.preventDefault();

                this.debug && console.log("Quantity button clicked:", event.currentTarget);

                const button = event.currentTarget;
                const previousValue = this.input.value;
                const newValue = "" + (parseInt(button.dataset.quantity) || 1);

                if (previousValue === newValue) {
                    this.debug && console.log("Quantity is already set to", newValue);
                    return;
                }

                this.input.value = newValue;
                this.input.dispatchEvent(this.changeEvent);
                this.debug && console.log("Updated quantity input value:", newValue);

                /*
                * Set the "active_quantity" class on the clicked button
                */
                this.querySelectorAll('button').forEach((button) => {
                    button.classList.toggle('active_quantity', button.dataset.quantity === newValue);
                });

                this.recalculateAndUpdatePrices();
                this.manageAddToCartButton();
            }

            manageAddToCartButton() {
                const submitButton = document.querySelector('.product-form__submit');

                if (this.isMalabergQuantitySelectorBlocked()) {
                    submitButton.setAttribute('disabled', 'disabled');
                    submitButton.querySelector('span').textContent = window.variantStrings.soldOut;
                } else {
                    submitButton.removeAttribute('disabled');
                    submitButton.querySelector('span').textContent = window.variantStrings.addToCart;
                }

                console.log(submitButton.querySelector('span').text)
            }

            isMalabergQuantitySelectorBlocked() {
                const variantId = (new URL(window.location.href)).searchParams.get('variant') ?? '';
                const maxCount = JSON.parse(this.dataset.quantities ?? '{}')[variantId] ?? 0;

                return parseInt(
                    this.querySelector('.quantity-input-malaberg-button.active_quantity')?.dataset.quantity ?? 0
                ) > maxCount;
            };

            /** Recalculate and update all relevant prices */
            recalculateAndUpdatePrices() {
                const quantity = parseInt(this.input.value) || 1;
                this.debug && console.log("Recalculating prices for quantity:", quantity);
                const activeButton = this.querySelector('.quantity-input-malaberg-button.active_quantity');

                if (!activeButton) return;

                this.debug && console.log("Last active select plan used for calculation:", this.lastSelectedSellingPlan);

                // Calculate and update the new One-Time price with the selected quantity discount
                const quantityDiscountVolumeOneTime = parseFloat(activeButton.dataset.discountVolumeOnetime) || 0;
                this.debug && console.log("Quantity Discount Volume One time:", quantityDiscountVolumeOneTime);
                const quantityDiscountTypeOneTime = activeButton.dataset.discountTypeOnetime || 'fixed_amount';
                this.debug && console.log("Quantity Discount Type One time:", quantityDiscountTypeOneTime);

                const newOneTimePrice = this.getNewTotalPriceWithDiscount(this.basePriceStorage[this.lastSelectedSellingPlan].originalPricePerItem, quantity, quantityDiscountVolumeOneTime, quantityDiscountTypeOneTime);
                this.debug && console.log("New One-Time Price:", newOneTimePrice);
                let newOneTimePriceContainer = document.querySelector('.appstle_one_time_price_wrapper .appstle_subscription_amount .money');

                if (!newOneTimePriceContainer) {
                    newOneTimePriceContainer = document.querySelector('.appstle_one_time_price_wrapper .appstle_subscription_amount');
                }

                if (newOneTimePriceContainer) {
                    newOneTimePriceContainer.textContent = newOneTimePrice;
                    this.debug && console.log("Updated One-Time Price");
                } else {
                    this.debug && console.error("One-Time Price Container not found");
                }

                // Calculate and update the new Subscription price with the selected quantity discount
                const quantityDiscountVolumeSubscription = parseFloat(activeButton.dataset.discountVolumeSubscription) || 0;
                this.debug && console.log("Quantity Discount Volume Subscription:", quantityDiscountVolumeSubscription);
                const quantityDiscountTypeSubscription = activeButton.dataset.discountTypeSubscription || 'fixed_amount';
                this.debug && console.log("Quantity Discount Type Subscription:", quantityDiscountTypeSubscription);

                //const newSubscriptionPrice = this.getNewTotalPriceWithDiscount(this.basePriceStorage[this.lastSelectedSellingPlan].baseSubscriptionPrice, quantity, quantityDiscountVolumeSubscription, quantityDiscountTypeSubscription);
                const newSubscriptionPrice = this.currencySymbol + parseFloat(
                    (this.basePriceStorage[this.lastSelectedSellingPlan].originalPricePerItem - quantityDiscountVolumeSubscription) * quantity
                ).toFixed(2);
                this.debug && console.log("New Subscription Price:", newSubscriptionPrice);
                let newSubscriptionPriceContainer = document.querySelector('.appstle_subscription_amount_wrapper .appstle_subscription_amount .money');

                if (!newSubscriptionPriceContainer) {
                    newSubscriptionPriceContainer = document.querySelector('.appstle_subscription_amount_wrapper .appstle_subscription_amount');
                }

                if (newSubscriptionPriceContainer) {
                    newSubscriptionPriceContainer.textContent = newSubscriptionPrice;
                    this.debug && console.log("Updated Subscription Price");
                } else {
                    this.debug && console.error("Subscription Price Container not found");
                }

                // Set the resulting current Total Price (dependent on whether the Subscription or One-Time price is selected)
                const newTotalPrice = this.getSelectedSellingPlan() ? newSubscriptionPrice : newOneTimePrice;
                let totalPriceContainer = document.querySelector('.price .money');

                if (!totalPriceContainer) {
                    totalPriceContainer = document.querySelector('.price');
                }

                if (totalPriceContainer) {
                    totalPriceContainer.textContent = newTotalPrice;
                    this.debug && console.log("Updated Total Price to: ", newTotalPrice);
                }

                /*
                 * Below are updates of the "Original" ("Compare at") prices. It's so complex - because the "compare at"
                 *   price can be configured for each subscription plan and one-time-purchase in the
                 *   Appstle Subscription app. We should take into account these prices.
                 */
                // Calculate and update the new total original (compare-at) price
                const newOriginalTotalPrice = this.basePriceStorage[this.lastSelectedSellingPlan].originalPricePerItem * quantity;
                this.debug && console.log("New Original Total Price:", newOriginalTotalPrice);

                // Update the original total price for the Subscription price
                let newOriginalTotalPriceSubscriptionContainer = document.querySelector('.appstle_subscription_amount_wrapper .appstle_subscription_compare_amount .money');

                if (!newOriginalTotalPriceSubscriptionContainer) {
                    newOriginalTotalPriceSubscriptionContainer = document.querySelector('.appstle_subscription_amount_wrapper .appstle_subscription_compare_amount');
                }

                if (newOriginalTotalPriceSubscriptionContainer) {
                    newOriginalTotalPriceSubscriptionContainer.textContent = `${this.currencySymbol}${newOriginalTotalPrice.toFixed(2)}`;
                    this.debug && console.log("Updated Original Total Price for Subscription Price");
                } else {
                    this.debug && console.error("Original Total Price Container for Subscription Price not found");
                }

                // Update the original total price for the One-Time price only if it's different from the One-Time price
                // (e.g. for the quantity 1 it will be the same - no need to show it)
                let newOriginalTotalPriceOneTimeContainer = document.querySelector('.appstle_one_time_price_wrapper .appstle_subscription_amount_compare_at_price .money');

                if (!newOriginalTotalPriceOneTimeContainer) {
                    newOriginalTotalPriceOneTimeContainer = document.querySelector('.appstle_one_time_price_wrapper .appstle_subscription_amount_compare_at_price');
                }

                if (newOriginalTotalPriceOneTimeContainer) {
                    if (newOriginalTotalPrice !== parseFloat(newOneTimePrice.replace(/[^0-9.]/g, ''))) {
                        newOriginalTotalPriceOneTimeContainer.textContent = `${this.currencySymbol}${newOriginalTotalPrice.toFixed(2)}`;
                        this.debug && console.log("Updated Original Total Price for One-Time Price");
                    } else {
                        newOriginalTotalPriceOneTimeContainer.textContent = '';
                        this.debug && console.log("Cleared Original Total Price for One-Time Price");
                    }
                } else {
                    this.debug && console.error("Original Total Price Container for One-Time Price not found");
                }

                // calculate the total discount in percent and update the badge and the first line in bullet points in the Subscription Plan section
                const subscriptionDiscountInPercent = Math.round((1 - (parseFloat(newSubscriptionPrice.replace(/[^0-9.]/g, '')) / newOriginalTotalPrice)) * 100);
                this.debug && console.log("Subscription Discount in Percent:", subscriptionDiscountInPercent);
                document.querySelector('.appstle-save-badge').textContent = `Save ${subscriptionDiscountInPercent}%`;
                setTimeout(() => {
                    document.querySelector('.ss-save-holder').innerHTML = subscriptionDiscountInPercent;
                }, 100);
                
                this.debug && console.log("Updated Save Badge");
                // Replace the phrase "Save X% on every order" in the Subscription Plan section with the new discount amount
                // We cannot just put the whole string, because the DIV contains also an image of the bullet point
                const originalSubscriptionDescriptionDiv = document.querySelector('.appstleSelectedSellingPlanOptionDescription > div');

                if (originalSubscriptionDescriptionDiv) {
                    const originalSubscriptionDescription = originalSubscriptionDescriptionDiv.innerHTML;
                    this.debug && console.log("Original Subscription Description:", originalSubscriptionDescription);
                    const newSubscriptionDescription = originalSubscriptionDescription.replace(/Save \d+%/g, `Save ${subscriptionDiscountInPercent}%`);
                    this.debug && console.log("New Subscription Description:", newSubscriptionDescription);
                    originalSubscriptionDescriptionDiv.innerHTML = newSubscriptionDescription;
                    this.debug && console.log("Updated Subscription Description");
                } else {
                    this.debug && console.log("Subscription Description element not found");
                }

                // Update the "Original" (compare-at) price near the "Add to Cart" button (if it differs from the newTotalPrice)
                const priceBlockContainer = document.querySelector('.price-block > div');

                if (!priceBlockContainer) {
                    this.debug && console.error("Price Block Container not found");
                    return;
                }

                const priceTopContainer = priceBlockContainer.querySelector('.price-top');
                /*
                 * We always remove the old and discount prices. Then we add them back if the original price differs
                 *  from the current total one.
                 */
                if (priceTopContainer) {
                    priceTopContainer.remove();
                }

                const calculatedCurrentDiscountTotal = newOriginalTotalPrice - parseFloat(newTotalPrice.replace(/[^0-9.]/g, ''));
                this.debug && console.log("Calculated Current Discount Total:", calculatedCurrentDiscountTotal);

                if (calculatedCurrentDiscountTotal > 0) {
                    const priceTop = document.createElement('div');
                    priceTop.classList.add('price-top');
                    priceTop.innerHTML = `
                        <div class="off" id="product-price-off"><span class="money">${this.currencySymbol}${calculatedCurrentDiscountTotal.toFixed(2)}</span> OFF</div>
                        <div class="price-old" id="product-old-price"><span class="money">${this.currencySymbol}${newOriginalTotalPrice.toFixed(2)}</span></div>
                    `;
                    priceBlockContainer.prepend(priceTop);
                }
            }

            getNewTotalPriceWithDiscount(price, quantity, discountVolume, discountType) {
                let newPrice = parseFloat(price) || 0;

                if (discountType === 'percentage') {
                    newPrice = newPrice * (1 - discountVolume / 100);
                } else if (discountType === 'fixed_amount') {
                    newPrice = Math.max(0, newPrice - discountVolume);
                }
                this.debug && console.log("New Price per item for Quantity:", newPrice);

                return `${this.currencySymbol}${(newPrice * quantity).toFixed(2)}`;
            }

            updateStoredBasePrices() {
                // Update the base Subscription price
                const activeSubscriptionPlatContainer = document.querySelector('.appstle-active-option .appstle_subscription_amount_wrapper');

                if (!activeSubscriptionPlatContainer) {
                    this.debug && console.error("Active Subscription Plan element not found");
                    return;
                }

                const selectedSellingPlan = this.getSelectedSellingPlan();

                if (!selectedSellingPlan) {
                    this.debug && console.error("Selected Selling Plan not found. The last selected plan will is:", this.lastSelectedSellingPlan);
                    return;
                }

                this.lastSelectedSellingPlan = selectedSellingPlan;
                this.debug && console.log("Selected Selling Plan:", selectedSellingPlan);

                // In the selected Subscription Plan key exists in this.basePriceStorage, we don't need to update the base prices
                if (this.basePriceStorage[selectedSellingPlan]) {
                    this.debug && console.log("Base Prices for the selected Subscription Plan already stored");
                    return;
                }

                this.basePriceStorage[selectedSellingPlan] = {};

                let subscriptionCurrentAmount = activeSubscriptionPlatContainer.querySelector('.appstle_subscription_amount .money');

                if (!subscriptionCurrentAmount) {
                    subscriptionCurrentAmount = activeSubscriptionPlatContainer.querySelector('.appstle_subscription_amount');
                }

                if (subscriptionCurrentAmount) {
                    this.basePriceStorage[selectedSellingPlan].baseSubscriptionPrice = parseFloat(subscriptionCurrentAmount.textContent.replace(/[^0-9.]/g, '')) || 0;
                    this.debug && console.log("Updated base Subscription price:", this.basePriceStorage[selectedSellingPlan].baseSubscriptionPrice);
                } else {
                    this.debug && console.error("Subscription Price element not found");
                }

                // Update the base Subscription Original (compare-at) price
                this.basePriceStorage[selectedSellingPlan].originalPricePerItem = this.compareAtPricePerItem;
                this.debug && console.log("Updated base Subscription compare-at price:", this.basePriceStorage[selectedSellingPlan].originalPricePerItem);
                // let subscriptionCompareAmount = activeSubscriptionPlatContainer.querySelector('.appstle_subscription_compare_amount .money');
                //
                // if (!subscriptionCompareAmount) {
                //     subscriptionCompareAmount = activeSubscriptionPlatContainer.querySelector('.appstle_subscription_compare_amount');
                // }
                //
                // if (subscriptionCompareAmount) {
                //     this.basePriceStorage[selectedSellingPlan].originalPricePerItem = parseFloat(subscriptionCompareAmount.textContent.replace(/[^0-9.]/g, '')) || 0;
                //     this.debug && console.log("Updated base Subscription compare-at price:", this.basePriceStorage[selectedSellingPlan].originalPricePerItem);
                // } else {
                //     this.debug && console.error("Subscription Compare Price element not found");
                // }
            }

            getSelectedSellingPlan() {
                const appstleWidget = document.querySelector('.appstle_sub_widget');

                if (!appstleWidget) {
                    this.debug && console.error("Subscription Widget element not found");
                    return;
                }

                this.debug && console.log("Selected Selling Plan:", appstleWidget.dataset.selectedSellingPlan);

                return appstleWidget.dataset.selectedSellingPlan;
            }
        }
    );
}
